// ============================================================================
// CHEAT SCAN PANEL
// ============================================================================
// Gameplay-analytics cheat/bot detection. Backed by the PokerIntel pipeline:
// every completed hand feeds playerStats/{uid}; /admin/runCheatScan scores
// each player on two explainable axes and persists ranked flags to
// cheatFlags/{uid}. This panel runs the scan and reviews the ranked list.
//
// botScore   - robotic timing (instant-action ratio, timing variance, VPIP)
// cheatScore - outcome anomalies (bb/100, showdown win %, chip dumping)
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import {
  VStack, HStack, Text, Badge, Spinner, Box,
  Table, Thead, Tbody, Tr, Th, Td, Tooltip,
} from '@chakra-ui/react';
import { Bot, ShieldAlert, RefreshCw, Radar, Ban, Eye, CheckCircle } from 'lucide-react';
import { POKER_COLORS } from '../../../theme/designSystem';
import PokerCard from '../../ui/PokerCard';
import PokerButton from '../../ui/PokerButton';
import gameApiService from '../../../services/gameApiService';

const scoreColor = (score: number): string =>
  score >= 70 ? 'red' : score >= 40 ? 'orange' : 'yellow';

const CheatScanPanel: React.FC = () => {
  const [flags, setFlags] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastScan, setLastScan] = useState<{ scanned: number; flagged: number } | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const loadFlags = useCallback(async () => {
    setLoading(true);
    setError(null);
    const r = await gameApiService.adminGetCheatFlags();
    setLoading(false);
    if (!r.success) {
      setError(r.error || 'Failed to load cheat flags');
      return;
    }
    setFlags(r.flags || []);
  }, []);

  useEffect(() => { loadFlags(); }, [loadFlags]);

  const runScan = async () => {
    setScanning(true);
    setError(null);
    const r = await gameApiService.adminRunCheatScan();
    setScanning(false);
    if (!r.success) {
      setError(r.error || 'Scan failed');
      return;
    }
    setLastScan({ scanned: r.scanned || 0, flagged: r.flaggedCount || 0 });
    setFlags(r.flags || []);
  };

  const [actioning, setActioning] = useState<string | null>(null);
  const takeAction = async (uid: string, action: 'ban' | 'watchlist' | 'dismiss') => {
    if (action === 'ban') {
      const reason = window.prompt('Ban reason (shown in the audit log):', 'Cheat detection - confirmed after review');
      if (reason === null) return; // cancelled
      const dur = window.prompt('Ban duration in hours (0 or empty = permanent):', '0');
      if (dur === null) return;
      setActioning(uid);
      const r = await gameApiService.adminResolveCheatFlag(uid, 'ban', reason, parseInt(dur, 10) || 0);
      setActioning(null);
      if (!r.success) { setError(r.error || 'Ban failed'); return; }
    } else {
      setActioning(uid);
      const r = await gameApiService.adminResolveCheatFlag(uid, action);
      setActioning(null);
      if (!r.success) { setError(r.error || 'Action failed'); return; }
    }
    await loadFlags();
  };

  return (
    <PokerCard>
      <VStack align="stretch" spacing={4} p={1}>
        <HStack justify="space-between">
          <HStack>
            <Radar size={18} color={POKER_COLORS.cyan} />
            <Text fontSize="md" fontWeight="bold" color="white">
              Cheat &amp; Bot Detection
            </Text>
            <Tooltip label="Scores rebuild from gameplay analytics collected on every completed hand: decision timing, VPIP, win rates, showdown results, chip-transfer patterns. House bots excluded.">
              <Badge colorScheme="cyan" fontSize="9px" cursor="help">POKERINTEL</Badge>
            </Tooltip>
          </HStack>
          <HStack>
            <PokerButton size="sm" variant="secondary" onClick={loadFlags} isDisabled={loading || scanning}>
              <RefreshCw size={13} style={{ marginRight: 6 }} /> Refresh
            </PokerButton>
            <PokerButton size="sm" onClick={runScan} isLoading={scanning}>
              <Radar size={13} style={{ marginRight: 6 }} /> Run Scan
            </PokerButton>
          </HStack>
        </HStack>

        {lastScan && (
          <Text fontSize="xs" color="gray.400">
            Last scan: {lastScan.scanned} players analyzed, {lastScan.flagged} flagged.
          </Text>
        )}
        {error && (
          <Box bg="red.900" border="1px solid" borderColor="red.700" borderRadius="md" p={2}>
            <Text fontSize="sm" color="red.200">{error}</Text>
          </Box>
        )}

        {loading ? (
          <Box textAlign="center" py={8}><Spinner color="cyan.400" /></Box>
        ) : flags.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Text color="gray.500" fontSize="sm">
              No flagged players. Run a scan after some hands have been played.
            </Text>
          </Box>
        ) : (
          <Box overflowX="auto">
            <Table size="sm" variant="unstyled">
              <Thead>
                <Tr borderBottom="1px solid" borderColor="whiteAlpha.200">
                  <Th color="gray.500" fontSize="10px">#</Th>
                  <Th color="gray.500" fontSize="10px">Player</Th>
                  <Th color="gray.500" fontSize="10px" isNumeric>
                    <HStack spacing={1} justify="flex-end"><Bot size={11} /><Text>Bot</Text></HStack>
                  </Th>
                  <Th color="gray.500" fontSize="10px" isNumeric>
                    <HStack spacing={1} justify="flex-end"><ShieldAlert size={11} /><Text>Cheat</Text></HStack>
                  </Th>
                  <Th color="gray.500" fontSize="10px" isNumeric>Total</Th>
                  <Th color="gray.500" fontSize="10px" isNumeric>Hands</Th>
                  <Th color="gray.500" fontSize="10px" isNumeric>bb net</Th>
                </Tr>
              </Thead>
              <Tbody>
                {flags.map((f, i) => (
                  <React.Fragment key={f.uid || f.id}>
                    <Tr
                      borderBottom="1px solid" borderColor="whiteAlpha.100"
                      cursor="pointer"
                      _hover={{ bg: 'whiteAlpha.50' }}
                      onClick={() => setExpanded(expanded === (f.uid || f.id) ? null : (f.uid || f.id))}
                    >
                      <Td color="gray.500" fontSize="xs">{i + 1}</Td>
                      <Td>
                        <HStack spacing={2}>
                          <Text fontSize="sm" color="white" fontWeight="semibold">
                            {f.name || (f.uid || f.id || '').substring(0, 10)}
                          </Text>
                          {f.status === 'watchlist' && <Badge colorScheme="yellow" fontSize="8px">WATCHLIST</Badge>}
                          {f.status === 'banned' && <Badge colorScheme="red" fontSize="8px">BANNED</Badge>}
                          {f.status === 'dismissed' && <Badge colorScheme="gray" fontSize="8px">DISMISSED</Badge>}
                        </HStack>
                        <Text fontSize="10px" color="gray.500">{(f.uid || f.id || '').substring(0, 16)}</Text>
                      </Td>
                      <Td isNumeric>
                        <Badge colorScheme={f.botScore > 0 ? scoreColor(f.botScore) : 'gray'} fontSize="11px">
                          {f.botScore || 0}
                        </Badge>
                      </Td>
                      <Td isNumeric>
                        <Badge colorScheme={f.cheatScore > 0 ? scoreColor(f.cheatScore) : 'gray'} fontSize="11px">
                          {f.cheatScore || 0}
                        </Badge>
                      </Td>
                      <Td isNumeric>
                        <Badge colorScheme={scoreColor(f.totalScore || 0)} fontSize="12px" px={2}>
                          {f.totalScore || 0}
                        </Badge>
                      </Td>
                      <Td isNumeric color="gray.300" fontSize="xs">{f.stats?.handsPlayed ?? '-'}</Td>
                      <Td isNumeric fontSize="xs"
                        color={(f.stats?.netBB || 0) > 0 ? 'green.300' : 'red.300'}>
                        {f.stats?.netBB ?? '-'}
                      </Td>
                    </Tr>
                    {expanded === (f.uid || f.id) && (
                      <Tr>
                        <Td colSpan={7} bg="whiteAlpha.50" borderRadius="md">
                          <VStack align="start" spacing={1} py={2}>
                            <Text fontSize="xs" color="gray.400" fontWeight="bold" letterSpacing="wide">
                              EVIDENCE
                            </Text>
                            {(f.reasons || []).map((r: string, j: number) => (
                              <HStack key={j} align="start">
                                <Text color={POKER_COLORS.cyan} fontSize="xs">-</Text>
                                <Text fontSize="xs" color="gray.200">{r}</Text>
                              </HStack>
                            ))}
                            {f.stats && (
                              <Text fontSize="10px" color="gray.500" pt={1}>
                                VPIP {f.stats.vpipPct}% · showdowns {f.stats.showdownsWon}/{f.stats.showdownsSeen} won
                                · {f.stats.instantActions}/{f.stats.decisionCount} instant actions
                              </Text>
                            )}

                            {/* ── ACTIONS ─────────────────────────────── */}
                            <HStack pt={2} spacing={2}>
                              <PokerButton
                                size="xs"
                                onClick={(e: any) => { e.stopPropagation(); takeAction(f.uid || f.id, 'ban'); }}
                                isLoading={actioning === (f.uid || f.id)}
                                isDisabled={f.status === 'banned' || !!actioning}
                                style={{ background: '#991b1b' }}
                              >
                                <Ban size={12} style={{ marginRight: 4 }} />
                                {f.status === 'banned' ? 'Banned' : 'Ban Account'}
                              </PokerButton>
                              <PokerButton
                                size="xs" variant="secondary"
                                onClick={(e: any) => { e.stopPropagation(); takeAction(f.uid || f.id, 'watchlist'); }}
                                isDisabled={f.status === 'watchlist' || f.status === 'banned' || !!actioning}
                              >
                                <Eye size={12} style={{ marginRight: 4 }} /> Watchlist
                              </PokerButton>
                              <PokerButton
                                size="xs" variant="secondary"
                                onClick={(e: any) => { e.stopPropagation(); takeAction(f.uid || f.id, 'dismiss'); }}
                                isDisabled={f.status === 'banned' || !!actioning}
                              >
                                <CheckCircle size={12} style={{ marginRight: 4 }} /> Dismiss
                              </PokerButton>
                            </HStack>
                          </VStack>
                        </Td>
                      </Tr>
                    )}
                  </React.Fragment>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </VStack>
    </PokerCard>
  );
};

export default CheatScanPanel;
